import { useCallback } from "react";

import { PREVIOUS_SUFFIX } from "../../constants/previous";
import {
  canAddDynamicAttribute,
  canAddDynamicGroup,
  getDynamicFieldValues,
} from "../../store/selectors/field-selectors";
import type { FormStore } from "../../store/types";
import type {
  AttributeCollectionId,
  AttributeGroupId,
  AttributeId,
  FieldId,
  FieldValueType,
} from "../../types";
import { createFieldId, parseFieldId } from "../../utils/field-id";

interface Params {
  store: FormStore;
}

export function useFormDynamicFields({ store }: Params) {
  // 動的フィールドグループ情報を取得
  const getDynamicFieldGroup = useCallback(
    (collectionId: AttributeCollectionId, groupId: AttributeGroupId) => {
      // コレクションとグループ設定を取得
      const collection = store.getCollection(collectionId);
      if (!collection) {
        return {
          fieldValues: [],
          canAdd: false,
          addGroup: () => {},
          removeGroup: () => {},
        };
      }

      const groupSetting = collection.attributeGroupSettings?.find(
        (g) => g.id === groupId,
      );
      if (!groupSetting) {
        return {
          fieldValues: [],
          canAdd: false,
          addGroup: () => {},
          removeGroup: () => {},
        };
      }

      // 動的フィールド値を取得
      const fieldValues = getDynamicFieldValues(
        store,
        collectionId,
        groupId,
        groupSetting.attributes?.[0]?.id || "",
      );

      // フィールド値をグループ化
      const groupValues = fieldValues.reduce<
        {
          collectionIndex: number;
          groupIndex: number;
          values: Record<FieldId, FieldValueType>;
        }[]
      >((acc, value) => {
        const parsedFieldId = parseFieldId(value.fieldId);
        if (!parsedFieldId) return acc;

        const existingGroup = acc.find(
          (group) =>
            group.collectionIndex === parsedFieldId.collectionIndex &&
            group.groupIndex === parsedFieldId.groupIndex,
        );

        if (existingGroup) {
          existingGroup.values[parsedFieldId.attributeId as FieldId] =
            value.value;
        } else {
          acc.push({
            collectionIndex: parsedFieldId.collectionIndex,
            groupIndex: parsedFieldId.groupIndex,
            values: { [parsedFieldId.attributeId]: value.value },
          });
        }

        return acc;
      }, []);

      // 動的グループを追加可能かどうかを判定
      const canAdd = canAddDynamicGroup(store, collectionId, groupId);

      // 動的グループを追加するハンドラー
      const addGroup = () => {
        if (!canAddDynamicGroup(store, collectionId, groupId)) {
          return;
        }

        store.addDynamicGroup(collectionId, groupSetting);

        // // 追加直後のバリデーションエラーをクリア
        const newValues = store.values.filter((value) => {
          const parsedFieldId = parseFieldId(value.fieldId);
          return (
            parsedFieldId?.collectionId === collectionId &&
            parsedFieldId?.groupId === groupId
          );
        });

        // 新しく追加されたフィールドのエラーをクリア
        newValues.forEach((value) => {
          store.removeFieldError(value.fieldId);
        });
      };

      // 削除ハンドラー
      const removeGroup = (groupIndex: number) => {
        if (!groupSetting.isDynamic) return;

        // 特定のグループインデックスに関連する値のみを削除
        const valuesToRemove = store.values.filter((value) => {
          const parsedFieldId = parseFieldId(value.fieldId);
          return (
            parsedFieldId?.collectionId === collectionId &&
            parsedFieldId?.groupId === groupId &&
            parsedFieldId?.groupIndex === groupIndex
          );
        });

        // 変更前の値がある場合は、Group削除ではなく値を消すだけにする
        const isIncludedFieldTypeOfChange = valuesToRemove.some(
          (value) =>
            value.fieldId.endsWith(PREVIOUS_SUFFIX) &&
            value.value !== undefined,
        );

        // 該当する値を個別に削除
        valuesToRemove
          .filter((value) => !value.fieldId.endsWith(PREVIOUS_SUFFIX))
          .forEach((value) => {
            if (isIncludedFieldTypeOfChange) {
              store.setValue(value.fieldId, undefined);
            } else {
              store.removeValue(value.fieldId);
            }
          });

        if (isIncludedFieldTypeOfChange) {
          return;
        }

        // グループ削除後、それより後ろのインデックスを持つグループのフィールドIDを調整
        const fieldsToUpdate = store.values.filter((value) => {
          const parsedFieldId = parseFieldId(value.fieldId);
          return (
            parsedFieldId?.collectionId === collectionId &&
            parsedFieldId?.groupId === groupId &&
            parsedFieldId?.groupIndex > groupIndex
          );
        });

        // 該当するフィールドを更新（インデックスを1つ減らす）
        fieldsToUpdate.forEach((value) => {
          const parsedFieldId = parseFieldId(value.fieldId);
          if (!parsedFieldId) return;

          // 古い値を削除
          store.removeValue(value.fieldId);

          // インデックスを1つ減らした新しいフィールドIDを生成
          const newFieldId = createFieldId({
            collectionId: parsedFieldId.collectionId,
            collectionIndex: parsedFieldId.collectionIndex,
            groupId: parsedFieldId.groupId,
            groupIndex: parsedFieldId.groupIndex - 1,
            attributeId: parsedFieldId.attributeId,
          });

          // 新しいフィールドIDで値を追加
          store.setValue(newFieldId, value.value);
        });
      };

      return {
        fieldValues: groupValues,
        canAdd,
        addGroup,
        removeGroup,
      };
    },
    [store],
  );

  // 動的コレクション操作
  const getDynamicCollections = useCallback(() => {
    // 動的コレクションの追加機能
    const addCollection = () => {
      const form = store.form;
      if (!form?.form?.attributeCollections) {
        console.warn("フォーム定義が存在しません");
        return;
      }

      // 全てのコレクションから動的なものを取得
      const dynamicCollections = form.form.attributeCollections.filter(
        (c) => c.isDynamic,
      );

      if (dynamicCollections.length === 0) {
        console.warn("動的コレクションが存在しません");
        return;
      }

      // 最初の動的コレクションを追加
      const collection = dynamicCollections[0];

      // 新しいコレクションのインデックスを計算
      const collectionIndices = store.values
        .map((value) => {
          const parsedId = parseFieldId(value.fieldId);
          return parsedId?.collectionId === collection.id
            ? parsedId.collectionIndex
            : -1;
        })
        .filter((idx) => idx !== -1);

      const newIndex =
        collectionIndices.length > 0 ? Math.max(...collectionIndices) + 1 : 0;

      // 新しいコレクション追加
      store.addDynamicCollection?.(collection);

      // 任意入力コレクション（minCount=0のコレクション）の場合、
      // 新しく追加されたコレクションを明示的に有効状態に設定する
      if (
        collection.minCount === 0 &&
        store.setCollectionEnabled &&
        store.isOptionalCollection
      ) {
        // コレクションが任意入力かどうか確認
        const isOptional = store.isOptionalCollection(collection.id);
        if (isOptional) {
          // 有効状態に設定（第3引数をtrueに）
          store.setCollectionEnabled(collection.id, newIndex, true);
        }
      }

      // コレクションに含まれるグループを全て追加する処理
      collection.attributeGroupSettings?.forEach((group) => {
        if (group.isDynamic) {
          for (let i = 0; i < (group.minCount || 1); i++) {
            // 正しい引数でaddDynamicGroupを呼び出す
            // コレクションインデックスは不要
            store.addDynamicGroup?.(collection.id, group);
          }
        }
      });
    };

    // 動的コレクションの削除機能
    const removeCollection = (collectionIndex: number) => {
      const collections = store.form?.form?.attributeCollections || [];

      // 対象コレクションを特定する
      let targetCollectionId: AttributeCollectionId | null = null;

      // コレクションIDの候補を取得（動的コレクションのみ）
      const dynamicCollectionIds = collections
        .filter((c) => c.isDynamic)
        .map((c) => c.id as AttributeCollectionId);

      if (dynamicCollectionIds.length === 0) {
        console.warn("動的コレクションが見つかりません");
        return;
      }

      // 各コレクションIDについて、フィールド値からインデックスを抽出
      const collectionIndicesMap = new Map<AttributeCollectionId, number[]>();

      dynamicCollectionIds.forEach((id) => {
        // そのコレクションIDを持つフィールドからインデックスを抽出
        const indices = store.values
          .map((value) => {
            const parsedId = parseFieldId(value.fieldId);
            return parsedId?.collectionId === id
              ? parsedId.collectionIndex
              : -1;
          })
          .filter((idx) => idx !== -1);

        // 一意のインデックスを抽出してソート
        const uniqueIndices = [...new Set(indices)].sort((a, b) => a - b);

        if (uniqueIndices.length > 0) {
          collectionIndicesMap.set(id, uniqueIndices);
        }
      });

      // コレクションインデックスから適切なコレクションIDを特定
      for (const [id, indices] of collectionIndicesMap.entries()) {
        if (collectionIndex < indices.length) {
          targetCollectionId = id;
          break;
        }
      }

      if (!targetCollectionId && dynamicCollectionIds.length > 0) {
        // 適切なIDが見つからない場合は最初の動的コレクションIDを使用
        targetCollectionId = dynamicCollectionIds[0];
      }

      if (!targetCollectionId) {
        console.warn("削除対象となる動的コレクションが特定できません");
        return;
      }

      // 対象コレクションのインデックス配列を取得
      const targetIndices = collectionIndicesMap.get(targetCollectionId) || [];

      // 論理インデックスから物理インデックスへの変換
      const physicalIndex =
        collectionIndex < targetIndices.length
          ? targetIndices[collectionIndex]
          : collectionIndex;

      // コレクションに関連するすべての値を削除
      store.values.forEach((value) => {
        const parsedFieldId = parseFieldId(value.fieldId);
        if (
          parsedFieldId?.collectionId === targetCollectionId &&
          parsedFieldId?.collectionIndex === physicalIndex
        ) {
          store.removeValue(value.fieldId);
        }
      });

      // コレクションIDとインデックスを指定して削除
      store.removeDynamicCollection(targetCollectionId, physicalIndex);
    };

    // 追加可能かどうかを判定
    const canAdd = (): boolean => {
      const form = store.form;
      if (!form?.form?.attributeCollections) {
        return false;
      }

      // 動的コレクションを取得
      const dynamicCollections = form.form.attributeCollections.filter(
        (c) => c.isDynamic,
      );

      if (dynamicCollections.length === 0) {
        return false;
      }

      // 参照用の最初の動的コレクション
      const firstDynamicCollection = dynamicCollections[0];

      // 現在の同じタイプの動的コレクション数
      const currentCount = form.form.attributeCollections.filter(
        (c) => c.id === firstDynamicCollection.id,
      ).length;

      // 最大数のチェック
      const maxCount = firstDynamicCollection.maxCount ?? Infinity;

      return currentCount < maxCount;
    };

    return {
      canAdd: canAdd(), // 実際の条件に基づいて判定
      addCollection,
      removeCollection,
    };
  }, [store]);

  // 動的フィールド情報を取得
  const getDynamicFields = useCallback(
    (
      collectionId: AttributeCollectionId,
      groupId: AttributeGroupId,
      attributeId: AttributeId,
    ) => {
      const canAdd = canAddDynamicAttribute(
        store,
        collectionId,
        groupId,
        attributeId,
      );

      const addField = () => {
        const collection = store.getCollection(collectionId);
        if (!collection) return;

        const groupSetting = collection.attributeGroupSettings?.find(
          (g) => g.id === groupId,
        );
        if (!groupSetting || !groupSetting.isDynamic) return;

        const attribute = groupSetting.attributes?.find(
          (a) => a.id === attributeId,
        );
        if (!attribute) return;

        store.addDynamicAttribute(collectionId, groupId, attribute);

        // 新しく追加された属性のフィールドIDを特定
        const newFields = store.values.filter((value) => {
          const parsedFieldId = parseFieldId(value.fieldId);
          return (
            parsedFieldId?.collectionId === collectionId &&
            parsedFieldId?.groupId === groupId &&
            parsedFieldId?.attributeId === attributeId
          );
        });

        // 追加したフィールドのバリデーションエラーをクリア
        newFields.forEach((value) => {
          store.removeFieldError(value.fieldId);
        });
      };

      const removeField = (fieldId: FieldId) => {
        const parsedFieldId = parseFieldId(fieldId);
        if (!parsedFieldId) return;
        store.removeDynamicAttribute(
          parsedFieldId.collectionId,
          parsedFieldId.groupId,
          parsedFieldId.attributeId,
        );
      };

      return {
        canAdd,
        addField,
        removeField,
      };
    },
    [store],
  );

  return {
    getDynamicFieldGroup,
    getDynamicCollections,
    getDynamicFields,
  };
}
